import React, { useState } from 'react'
import Seo from '../components/Seo'
import { ArrowRight, Shield, Calendar, CheckCircle, Menu, X, ChevronDown, ChevronUp } from "lucide-react"
import logoImage from '../assets/logo/logo.png'
import { track } from '../utils/analytics'
import { showPreferences } from '../utils/cookieConsentConfig'

const Home: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null)
  const [isDemoOpen, setIsDemoOpen] = useState(false)

  const toggleFaq = (index: number, question: string) => {
    const willOpen = openFaqIndex !== index
    setOpenFaqIndex(willOpen ? index : null)
    if (willOpen) {
      track('faq_expand', { q: question })
    }
  }

  return (
    <div id="top" className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20">
      <Seo
        title="2domy.pl — kalendarz opieki naprzemiennej dla rodziców i opiekunów"
        description="Kalendarz opieki naprzemiennej dla rodziców i opiekunów: wspólny grafik i bezpieczna komunikacja. 2domy.pl działa na każdym urządzeniu."
        canonical="https://2domy.pl/"
        openGraph={{ type: 'website' }}
      />
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-sm border-b border-white/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="flex items-center">
              <img src={logoImage} alt="2domy.pl logo" className="h-8 w-auto mr-3" />
              <span className="font-bold text-xl bg-gradient-to-r from-blue-600 via-purple-600 to-blue-800 bg-clip-text text-transparent">2domy.pl</span>
            </a>

            {/* Desktop Menu */}
            <div className="hidden sm:flex items-center space-x-2">
              <a href="/blog" className="px-2 text-sm text-slate-700 hover:text-slate-900 hover:underline" onClick={() => track('cta_click', { label: 'blog_nav' })}>Blog</a>
              <a href="/pricing" className="px-2 text-sm text-slate-700 hover:text-slate-900 hover:underline" onClick={() => track('cta_click', { label: 'pricing_nav' })}>Cennik</a>
              <a href="/auth/login" className="glass-button px-4 py-2 text-sm rounded-md" onClick={() => track('cta_click', { label: 'login_nav' })}>Zaloguj</a>
            </div>

            {/* Mobile menu button */}
            <div className="sm:hidden">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-slate-600 hover:text-slate-800"
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Menu: overlay + animated panel (fixed, over content) */}
          <div
            className={`sm:hidden fixed inset-x-0 top-16 bottom-0 z-[55] bg-black/30 transition-opacity duration-300 ${mobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
            onClick={() => setMobileMenuOpen(false)}
            aria-hidden={!mobileMenuOpen}
          />
          <div
            className={`sm:hidden fixed inset-x-0 top-16 z-[60] bg-white/95 backdrop-blur-sm border-t border-white/20 transform transition-all duration-300 ease-out ${mobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'}`}
            role="menu"
            aria-label="Nawigacja mobilna"
          >
            <div className="px-2 pt-2 pb-3 space-y-1">
              <a href="/blog" className="w-full justify-center mb-2 inline-flex items-center text-center rounded-md text-slate-700 hover:text-slate-900" onClick={() => { track('cta_click', { label: 'blog_nav_mobile' }); setMobileMenuOpen(false) }}>Blog</a>
              <a href="/pricing" className="w-full justify-center mb-2 inline-flex items-center text-center rounded-md text-slate-700 hover:text-slate-900" onClick={() => { track('cta_click', { label: 'pricing_nav_mobile' }); setMobileMenuOpen(false) }}>Cennik</a>
              <a href="/auth/login" className="glass-button w-full justify-center mb-2 inline-flex items-center text-center rounded-md" onClick={() => { track('cta_click', { label: 'login_nav_mobile' }); setMobileMenuOpen(false) }}>Zaloguj</a>
            </div>
          </div>
        </div>
      </nav>



      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-8">
          <div className="grid lg:grid-cols-3 gap-8 lg:gap-12 items-center">
            {/* Left Column - Content */}
            <div className="space-y-6 lg:space-y-8 order-1 lg:order-1 lg:col-span-2">
              <div className="space-y-4 lg:space-y-6">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight lg:leading-[1.2] flex flex-wrap items-center gap-3 justify-center text-center lg:justify-start lg:text-left">
                  <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-800 bg-clip-text text-transparent">
                    Kalendarz opieki naprzemiennej online
                  </span>
                </h1>

                <p className="hidden lg:block text-lg sm:text-xl text-slate-600 leading-relaxed max-w-lg">
                  2domy.pl — szybki, przejrzysty grafik opieki naprzemiennej.  Dwa domy, jeden wspólny plan.
                </p>
              </div>

              {/* Mobile Hero Image */}
              <div className="block lg:hidden">
                <div className="relative mx-auto w-full max-w-[156px] sm:max-w-[220px]">
                  <div className="absolute -inset-4 -z-10 rounded-3xl bg-gradient-to-tr from-blue-400/30 via-purple-400/20 to-pink-400/20 blur-2xl"></div>
                  <div className="rounded-3xl overflow-hidden shadow-2xl ring-1 ring-black/5">
                    <img
                      src="/screen.jpg"
                      alt="Zrzut ekranu aplikacji 2domy.pl"
                      width="640" height="1280"
                      className="block w-full h-auto select-none"
                      loading="eager"
                    />
                  </div>
                </div>
              </div>

              {/* Features List */}
              <div id="funkcje" className="hidden lg:block space-y-3 lg:space-y-4">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-700 text-sm sm:text-base">Kalendarz opieki naprzemiennej</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-700 text-sm sm:text-base">Intuicyjny interface dla rodziców i opiekunów</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-700 text-sm sm:text-base">Bezpieczne szyfrowanie danych</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-700 text-sm sm:text-base">Neutralna przestrzeń do komunikacji.</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-700 text-sm sm:text-base">Wygodna aplikacja mobilna lub PWA — zainstaluj bez Google Play czy App Store</span>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <a href="/auth/register" className="neon-button group px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg w-full sm:w-auto rounded-xl inline-flex items-center justify-center" onClick={() => track('cta_click', { label: 'register_hero' })}>
                  Załóż darmowe konto
                  <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform" />
                </a>
                <a href="#faq" className="glass-button px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg backdrop-blur-sm bg-white/20 border-white/30 w-full sm:w-auto rounded-xl inline-flex items-center justify-center" onClick={() => track('cta_click', { label: 'faq_hero' })}>
                  FAQ
                </a>
              </div>
            </div>

            {/* Right Column - App Screenshot */}
            <div className="relative order-2 lg:order-2 lg:col-span-1 hidden lg:block">
              <div className="relative mx-auto w-full max-w-[240px] sm:max-w-sm lg:max-w-[16rem]">
                {/* Glow background */}
                <div className="absolute -inset-4 -z-10 rounded-3xl bg-gradient-to-tr from-blue-400/30 via-purple-400/20 to-pink-400/20 blur-2xl"></div>
                <div className="rounded-3xl overflow-hidden shadow-2xl ring-1 ring-black/5 transition-transform duration-300 hover:-rotate-1 hover:scale-[1.01]">
                  <img
                    src="/screen.jpg"
                    alt="Zrzut ekranu aplikacji 2domy.pl"
                    width="640" height="1280"
                    className="block w-full h-auto select-none"
                    loading="eager"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Floating shapes */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-r from-pink-400/20 to-red-400/20 rounded-full blur-3xl animate-pulse delay-2000"></div>
      </div>

      {/* Video Section */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-10">
        <div className="rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/5 bg-white/70 backdrop-blur">
          <div className="aspect-video w-full">
            <iframe
              src="https://www.youtube.com/embed/FOk2WwCD6RM?si=LUtKRuVjhYWLT2tO&rel=0&modestbranding=1&playsinline=1"
              title="2domy.pl – demo"
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
            />
          </div>
        </div>
      </div>

      {/* Mobile Intro Text and Features (below video) */}
      <div className="lg:hidden max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <p className="text-lg text-slate-600 leading-relaxed mb-4">
          2domy.pl — szybki, przejrzysty grafik opieki naprzemiennej.  Dwa domy, jeden wspólny plan.
        </p>
        <div className="space-y-3">
          <div className="flex items-center space-x-3">
            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
            <span className="text-slate-700 text-sm sm:text-base">Kalendarz opieki naprzemiennej</span>
          </div>
          <div className="flex items-center space-x-3">
            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
            <span className="text-slate-700 text-sm sm:text-base">Intuicyjny interface dla rodziców i opiekunów</span>
          </div>
          <div className="flex items-center space-x-3">
            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
            <span className="text-slate-700 text-sm sm:text-base">Bezpieczne szyfrowanie danych</span>
          </div>
          <div className="flex items-center space-x-3">
            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
            <span className="text-slate-700 text-sm sm:text-base">Neutralna przestrzeń do komunikacji.</span>
          </div>
          <div className="flex items-center space-x-3">
            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
            <span className="text-slate-700 text-sm sm:text-base">Wygodna aplikacja mobilna lub PWA — zainstaluj bez Google Play czy App Store</span>
          </div>
        </div>
      </div>

      {/* Blog Teasers Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-end justify-between mb-8">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-slate-700 to-slate-900 bg-clip-text text-transparent">Z bloga</h2>
          <a href="/blog" className="text-blue-600 hover:underline text-sm" onClick={() => track('cta_click', { label: 'blog_home_more' })}>Zobacz wszystkie</a>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {[
            { slug: 'opieka-naprzemienna-praktyka', title: 'Opieka naprzemienna — praktyka', desc: 'Podstawy, modele i codzienność.', img: '/blog/opieka-naprzemienna-praktyka/hero.webp' },
            { slug: 'plan-wychowawczy-opieka-naprzemienna', title: 'Plan wychowawczy — jak go przygotować', desc: 'Elementy planu, przykłady, narzędzia.', img: '/blog/plan-wychowawczy-opieka-naprzemienna/hero.webp' },
            { slug: '10-problemow-opieka-naprzemienna', title: '10 problemów i jak je rozwiązać', desc: 'Konflikt, grafiki, koszty — konkretne wskazówki.', img: '/blog/10-problemow-opieka-naprzemienna/hero.webp' },
          ].map(card => (
            <a key={card.slug} href={`/blog/${card.slug}`} className="group rounded-xl overflow-hidden border bg-white/70 backdrop-blur hover:shadow-lg transition">
              <div className="relative aspect-[16/9] bg-slate-100">
                <img src={card.img} alt={card.title} width="1280" height="720" loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
              </div>
              <div className="p-4">
                <h3 className="text-lg font-semibold text-slate-900 group-hover:underline">{card.title}</h3>
                <p className="text-slate-600 text-sm mt-1">{card.desc}</p>
              </div>
            </a>
          ))}
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold bg-gradient-to-r from-slate-700 to-slate-900 bg-clip-text text-transparent mb-4 mx-auto">
            Kluczowe funkcje
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Prosty kalendarz opieki naprzemiennej dla rodziców i opiekunów: wspólny grafik, plan wychowawczy i bezpieczna komunikacja. 2domy.pl działa na każdym urządzeniu.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* 1. Kalendarz współdzielony */}
          <div className="card-glass p-8 text-center group hover:scale-105 transition-transform duration-300">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Calendar className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-xl font-semibold mb-4 text-slate-800">Kalendarz współdzielony</h3>
            <p className="text-slate-600 leading-relaxed">
              Zapraszaj drugiego rodzica i obserwatorów. Przejrzysty harmonogram z oznaczeniami opiekunów.
            </p>
          </div>

          {/* 2. Notatki i koszty */}
          <div className="card-glass p-8 text-center group hover:scale-105 transition-transform duration-300">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Calendar className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-xl font-semibold mb-4 text-slate-800">Notatki i koszty</h3>
            <p className="text-slate-600 leading-relaxed">
              Dodawaj wydarzenia, zapisuj notatki i rejestruj koszty w jednym miejscu. Kolory typów i czytelna lista dnia pomagają szybko ogarnąć plan.
            </p>
          </div>

          {/* 3. Raporty */}
          <div className="card-glass p-8 text-center group hover:scale-105 transition-transform duration-300">
            <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-blue-700 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Calendar className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-xl font-semibold mb-4 text-slate-800">Raporty</h3>
            <p className="text-slate-600 leading-relaxed">
              Raport kosztów oraz Raport opieki (dni na dziecko lub podopiecznego, procenty). Dokumentuj miesiąc i rozliczenia w kilka kliknięć. W każdym planie możliwy eksport danych do JSON.
            </p>
          </div>

          {/* 4. Bezpieczeństwo */}
          <div className="card-glass p-8 text-center group hover:scale-105 transition-transform duration-300">
            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Shield className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-xl font-semibold mb-4 text-slate-800">Bezpieczeństwo</h3>
            <p className="text-slate-600 leading-relaxed">
              Szyfrowanie end‑to‑end chroni prywatność Twojej rodziny. Twoje dane pozostają tylko Twoje.
            </p>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="card-glass p-12 text-center">
          <h2 className="text-3xl font-bold mb-6 bg-gradient-to-r from-slate-700 to-slate-900 bg-clip-text text-transparent">
            Załóż darmowe konto lub zobacz demo
          </h2>
          <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
            Dołącz do rodzin, które już ufają 2domy.pl w organizacji życia z dziećmi.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/auth/register" className="neon-button px-8 py-4 text-lg w-full sm:w-auto rounded-xl inline-flex items-center justify-center" onClick={() => track('cta_click', { label: 'register_cta' })}>Załóż darmowe konto</a>
            <a
              href="#"
              className="glass-button px-8 py-4 text-lg w-full sm:w-auto rounded-xl inline-flex items-center justify-center"
              onClick={(e) => {
                e.preventDefault()
                track('view_demo', { label: 'demo_cta' })
                setIsDemoOpen(true)
              }}
            >
              Zobacz demo
            </a>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div id="faq" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <h2 className="text-3xl font-bold mb-6 bg-gradient-to-r from-slate-700 to-slate-900 bg-clip-text text-transparent">FAQ</h2>
        <div className="divide-y divide-slate-200 rounded-xl border border-slate-200 bg-white/60 backdrop-blur-sm">
          {/* Item 1 */}
          <div className="p-4">
            <button
              className="w-full flex items-center justify-between text-left"
              aria-expanded={openFaqIndex === 0}
              onClick={() => toggleFaq(0, 'Czy 2domy.pl jest darmowe?')}
            >
              <span className="text-base sm:text-lg font-semibold text-slate-800">Czy 2domy.pl jest darmowe?</span>
              {openFaqIndex === 0 ? (
                <ChevronUp className="h-5 w-5 text-slate-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-slate-500" />
              )}
            </button>
            {openFaqIndex === 0 && (
              <div className="mt-3 text-slate-600 text-sm sm:text-base">
                Tak — plan Basic jest dostępny dla każdego zarejestrowanego użytkownika i obejmuje: dostęp 2 rodziców/opiekunów, kalendarz dla 1 dziecka lub podopiecznego i mini historię (10 ostatnich wpisów). W planach płatnych (Pro/Max) zyskujesz m.in.: więcej kalendarzy dzieci/podopiecznych, obserwatorów kalendarza (poza rodzicami/opiekunami), dodawanie wydarzeń, notatek i kosztów, dłuższą historię i gotowe raporty (kosztów oraz opieki).
              </div>
            )}
          </div>
          {/* Item 2 */}
          <div className="p-4">
            <button
              className="w-full flex items-center justify-between text-left"
              aria-expanded={openFaqIndex === 1}
              onClick={() => toggleFaq(1, 'Czy moje dane są bezpieczne?')}
            >
              <span className="text-base sm:text-lg font-semibold text-slate-800">Czy moje dane są bezpieczne?</span>
              {openFaqIndex === 1 ? (
                <ChevronUp className="h-5 w-5 text-slate-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-slate-500" />
              )}
            </button>
            {openFaqIndex === 1 && (
              <div className="mt-3 text-slate-600 text-sm sm:text-base">
                Tak. Szyfrujemy dane w spoczynku i w tranzycie. Masz pełny eksport swoich danych na żądanie.
              </div>
            )}
          </div>
          {/* Item 3 */}
          <div className="p-4">
            <button
              className="w-full flex items-center justify-between text-left"
              aria-expanded={openFaqIndex === 2}
              onClick={() => toggleFaq(2, 'Czy 2domy.pl zastępuje porozumienie rodzicielskie?')}
            >
              <span className="text-base sm:text-lg font-semibold text-slate-800">Czy 2domy.pl zastępuje porozumienie rodzicielskie?</span>
              {openFaqIndex === 2 ? (
                <ChevronUp className="h-5 w-5 text-slate-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-slate-500" />
              )}
            </button>
            {openFaqIndex === 2 && (
              <div className="mt-3 text-slate-600 text-sm sm:text-base">
                Nie, ale pomaga trzymać się ustaleń i dokumentować zmiany.
              </div>
            )}
          </div>
          {/* Item 4 */}
          <div className="p-4">
            <button
              className="w-full flex items-center justify-between text-left"
              aria-expanded={openFaqIndex === 3}
              onClick={() => toggleFaq(3, 'Jak zainstalować aplikację na telefonie?')}
            >
              <span className="text-base sm:text-lg font-semibold text-slate-800">Jak zainstalować aplikację na telefonie?</span>
              {openFaqIndex === 3 ? (
                <ChevronUp className="h-5 w-5 text-slate-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-slate-500" />
              )}
            </button>
            {openFaqIndex === 3 && (
              <div className="mt-3 text-slate-600 text-sm sm:text-base">
                W Ustawieniach → Instalacja znajdziesz dedykowany przycisk do instalacji aplikacji. Na Androidzie w Chrome wybierz „Dodaj do ekranu głównego". Na iOS w Safari stuknij „Udostępnij" → „Do ekranu początkowego". 2domy.pl to PWA i działa jak aplikacja na większości nowszych systemów Android i iOS, ale nie gwarantujemy działania trybu aplikacji PWA na każdym urządzeniu. Zawsze możesz korzystać z kalendarza w formie strony internetowej.
              </div>
            )}
          </div>
          {/* Item 5 */}
          <div className="p-4">
            <button
              className="w-full flex items-center justify-between text-left"
              aria-expanded={openFaqIndex === 4}
              onClick={() => toggleFaq(4, 'Czy mogę zaprosić drugiego rodzica i obserwatorów?')}
            >
              <span className="text-base sm:text-lg font-semibold text-slate-800">Czy mogę zaprosić drugiego rodzica i obserwatorów?</span>
              {openFaqIndex === 4 ? (
                <ChevronUp className="h-5 w-5 text-slate-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-slate-500" />
              )}
            </button>
            {openFaqIndex === 4 && (
              <div className="mt-3 text-slate-600 text-sm sm:text-base">
                Tak. W Ustawieniach rodziny skorzystaj z Zaproszeń i przypisz odpowiednią rolę (rodzic, obserwator).
              </div>
            )}
          </div>
          {/* Item 6 */}
          <div className="p-4">
            <button
              className="w-full flex items-center justify-between text-left"
              aria-expanded={openFaqIndex === 5}
              onClick={() => toggleFaq(5, 'Kiedy otrzymuję dostęp do usług?')}
            >
              <span className="text-base sm:text-lg font-semibold text-slate-800">Kiedy dokładnie otrzymuję dostęp do usług?</span>
              {openFaqIndex === 5 ? (
                <ChevronUp className="h-5 w-5 text-slate-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-slate-500" />
              )}
            </button>
            {openFaqIndex === 5 && (
              <div className="mt-3 text-slate-600 text-sm sm:text-base">
                Plan Basic jest dostępny natychmiast po rejestracji. Dostęp do funkcji planów płatnych (Pro/Max) jest aktywowany natychmiast po dokonaniu płatności i jej potwierdzeniu przez operatora płatności.
              </div>
            )}
          </div>
          {/* Item 7 */}
          <div className="p-4">
            <button
              className="w-full flex items-center justify-between text-left"
              aria-expanded={openFaqIndex === 6}
              onClick={() => toggleFaq(6, 'Czy mogę eksportować swoje dane?')}
            >
              <span className="text-base sm:text-lg font-semibold text-slate-800">Czy mogę eksportować swoje dane?</span>
              {openFaqIndex === 6 ? (
                <ChevronUp className="h-5 w-5 text-slate-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-slate-500" />
              )}
            </button>
            {openFaqIndex === 6 && (
              <div className="mt-3 text-slate-600 text-sm sm:text-base">
                Tak. Masz pełny eksport swoich danych na żądanie.
              </div>
            )}
          </div>
          {/* Item 8 */}
          <div className="p-4">
            <button
              className="w-full flex items-center justify-between text-left"
              aria-expanded={openFaqIndex === 7}
              onClick={() => toggleFaq(7, 'Ile to kosztuje?')}
            >
              <span className="text-base sm:text-lg font-semibold text-slate-800">Ile to kosztuje?</span>
              {openFaqIndex === 7 ? (
                <ChevronUp className="h-5 w-5 text-slate-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-slate-500" />
              )}
            </button>
            {openFaqIndex === 7 && (
              <div className="mt-3 text-slate-600 text-sm sm:text-base">
                Plan Basic jest darmowy i dostępny dla każdego zarejestrowanego użytkownika. Szczegóły znajdziesz na stronie <a href="/pricing" className="text-blue-600 hover:underline">Cennik</a>.
              </div>
            )}
          </div>
          {/* Item 9 */}
          <div className="p-4">
            <button
              className="w-full flex items-center justify-between text-left"
              aria-expanded={openFaqIndex === 8}
              onClick={() => toggleFaq(8, 'Jak skontaktować się ze wsparciem?')}
            >
              <span className="text-base sm:text-lg font-semibold text-slate-800">Jak skontaktować się ze wsparciem?</span>
              {openFaqIndex === 8 ? (
                <ChevronUp className="h-5 w-5 text-slate-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-slate-500" />
              )}
            </button>
            {openFaqIndex === 8 && (
              <div className="mt-3 text-slate-600 text-sm sm:text-base">
                Napisz do nas na adres e‑mail: <a href="mailto:kontakt@2domy.pl" className="text-blue-600 hover:underline">kontakt@2domy.pl</a> lub skorzystaj z <a href="/contact" className="text-blue-600 hover:underline">formularza kontaktowego</a>.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-slate-800 to-slate-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-300">
            &copy; {new Date().getFullYear()} 2domy.pl - Kalendarz opieki naprzemiennej dla rodziców i opiekunów
          </p>
          <div className="mt-3 flex items-center justify-center gap-2 text-slate-400 text-xs flex-wrap">
            <a href="/terms" className="hover:underline">Regulamin</a>
            <span>•</span>
            <a href="/polityka-prywatnosci" className="hover:underline">Polityka prywatności</a>
            <span>•</span>
            <button
              className="hover:underline bg-transparent border-0 p-0 text-inherit cursor-pointer"
              onClick={(e) => {
                e.preventDefault()
                showPreferences()
              }}
            >
              Zarządzaj zgodą
            </button>
            <span>•</span>
            <a href="/contact" className="hover:underline">Kontakt</a>
          </div>
        </div>
      </footer>
      {isDemoOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsDemoOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-[90vw] max-w-3xl aspect-video">
            <button
              aria-label="Zamknij"
              className="absolute -top-3 -right-3 bg-white rounded-full shadow p-2 text-slate-700 hover:text-slate-900"
              onClick={() => setIsDemoOpen(false)}
            >
              <X className="h-5 w-5" />
            </button>
            <iframe
              src="https://www.youtube.com/embed/FOk2WwCD6RM?si=LUtKRuVjhYWLT2tO&autoplay=1&rel=0"
              title="2domy.pl – demo"
              className="w-full h-full rounded-2xl"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default Home 